// src/theme/index.js
import { hexWithAlpha, hexToRgb } from '../lib/color-utils.js';

// ===== helpers =====
const toHex2 = (n) => n.toString(16).padStart(2, '0').toUpperCase();

function cssColorToHex(input) {
  if (!input) return null;
  const c = input.trim();
  if (c.startsWith('#')) {
    let h = c.slice(1);
    if (h.length === 3) h = h.split('').map(ch => ch + ch).join('');
    if (h.length === 4) h = h.slice(0, 3).split('').map(ch => ch + ch).join('');
    if (h.length === 6) return `#${h.toUpperCase()}`;
    if (h.length === 8) return `#${h.slice(0, 6).toUpperCase()}`;
    return null;
  }
  const m = c.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i);
  if (m) {
    const r = Math.round(parseFloat(m[1]));
    const g = Math.round(parseFloat(m[2]));
    const b = Math.round(parseFloat(m[3]));
    return `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`;
  }
  return null;
}

// FG over BG with alpha => opaque equivalent
function opaqueEquivalent(fgHex, alpha, bgHex) {
  const fg = hexToRgb(fgHex);
  const bg = hexToRgb(bgHex);
  const mix = (f, b) => Math.round(alpha * f + (1 - alpha) * b);
  const r = mix(fg.r, bg.r), g = mix(fg.g, bg.g), b = mix(fg.b, bg.b);
  return `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`;
}

// Luminance to judge "lightness"
function relativeLuminance({ r, g, b }) {
  const srgb = [r, g, b].map(v => v / 255);
  const lin = srgb.map(c => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}
function isLight(hex, threshold = 0.5) {
  return relativeLuminance(hexToRgb(hex)) > threshold;
}

// ===== RGB <-> HSV та узгоджене затемнення+десат =====
function rgbToHsv({ r, g, b }) {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    switch (max) {
      case R: h = ((G - B) / d + (G < B ? 6 : 0)); break;
      case G: h = ((B - R) / d + 2); break;
      case B: h = ((R - G) / d + 4); break;
    }
    h /= 6;
  }
  const s = max === 0 ? 0 : d / max;
  const v = max;
  return { h, s, v }; // h in [0,1), s,v in [0,1]
}
function hsvToRgb({ h, s, v }) {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  let R, G, B;
  switch (i % 6) {
    case 0: R = v; G = t; B = p; break;
    case 1: R = q; G = v; B = p; break;
    case 2: R = p; G = v; B = t; break;
    case 3: R = p; G = q; B = v; break;
    case 4: R = t; G = p; B = v; break;
    case 5: R = v; G = p; B = q; break;
  }
  return {
    r: Math.round(R * 255),
    g: Math.round(G * 255),
    b: Math.round(B * 255),
  };
}
/** Темнішаємо на amount і одночасно зменшуємо насиченість на той самий відсоток */
function darkenAndDesaturate(hex, amount = 0.10) {
  const hsv = rgbToHsv(hexToRgb(hex));
  const k = Math.max(0, Math.min(1, amount));
  const s2 = Math.max(0, Math.min(1, hsv.s * (1 - k)));
  const v2 = Math.max(0, Math.min(1, hsv.v * (1 - k)));
  const { r, g, b } = hsvToRgb({ h: hsv.h, s: s2, v: v2 });
  return `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`;
}

// === тут нове: завжди працюємо з BODY і чистимо HTML ===
function getBodyThemeHost(theme = 'light') {
  const body = document.body || document.querySelector('body');
  if (!body) return document.documentElement; // fallback
  if (!body.hasAttribute('data-theme')) {
    body.setAttribute('data-theme', theme);
  }
  return body;
}

function scrubHtmlInlineVars() {
  const html = document.documentElement;
  [
    '--primary-color','--primary-color-66','--primary-color-3a','--primary-color-07',
    '--transparent-color','--transparent-hover-color','--transparent-color-absolute',
    '--border-color','--secondary-color'
  ].forEach(v => html.style.removeProperty(v));
}

export function applyUserTheme(baseHex, theme = 'light') {
  if (!baseHex) return;

  const host = getBodyThemeHost(theme); // пишемо тільки в <body>
  scrubHtmlInlineVars();                // прибираємо можливі інлайни з <html>

  const { r, g, b } = hexToRgb(baseHex);

  // 1) Основний колір — без змін
  host.style.setProperty('--primary-color', baseHex);
  host.style.setProperty('--primary-color-66', hexWithAlpha(baseHex, 102 / 255));
  host.style.setProperty('--primary-color-3a', hexWithAlpha(baseHex, 58 / 255));
  host.style.setProperty('--primary-color-07', hexWithAlpha(baseHex, 7 / 255));

  // 2) Прозорі версії
  const alphaCard  = 26 / 255;  // ≈0.102
  const alphaHover = 48 / 255;  // ≈0.188
  const amountForLight = 0.30;  // якщо колір світлий — темнішаємо+десатуємо на цей відсоток
  const baseForTrans = isLight(baseHex)
    ? darkenAndDesaturate(baseHex, amountForLight)
    : baseHex;

  host.style.setProperty('--transparent-color',       hexWithAlpha(baseForTrans, alphaCard));
  host.style.setProperty('--transparent-hover-color', hexWithAlpha(baseForTrans, alphaHover));

  // 3) Абсолютний еквівалент прозорої картки для поточного фону
  const computed = getComputedStyle(host);
  const baseColorVar = computed.getPropertyValue('--base-color').trim();
  const baseBgHex = cssColorToHex(baseColorVar) || '#FFFFFF';
  const absoluteCard = opaqueEquivalent(baseForTrans, alphaCard, baseBgHex);
  host.style.setProperty('--transparent-color-absolute', absoluteCard);

  // 4) Бордер
  host.style.setProperty('--border-color', `rgba(${r}, ${g}, ${b}, 0.24)`);

  // 5) Secondary
  const secondary = darkenAndDesaturate(baseHex, 0.15);
  host.style.setProperty('--secondary-color', secondary);
}
